import { useCallback, useEffect, useState } from 'react';
import { Product, Products } from '../../../api/graphql/products/types';
import {
  SubscribableProducts,
  SubscriptionBoxProduct,
} from '../../../utils/subscriptionDetail/types';
import { ProductDescModalViewType } from '../../../types/view/ProductDescModalViewType';
import { BoxSelectProductsModalViewType } from '../../../types/view/BoxSelectProductsModalViewType';
import { fetchGraphqlProductPage } from '../../../api/graphql/productPage';
import { postMessageGtag } from '../../../utils/trackClick';
import { resize, scrollCategoryAccordion } from '../../../utils/postMessage';
import { BoxSetContentsModalViewType } from '../../../types/view/BoxSetContentsModalViewType';

type Args = {
  subscribableProducts: SubscribableProducts | null;
  products: Products | null;
  paramCategoryId: string;
};

type ProductModal =
  | { kind: 'description'; data: ProductDescModalViewType }
  | { kind: 'boxUpdates'; data: BoxSelectProductsModalViewType }
  | { kind: 'boxSetContents'; data: BoxSetContentsModalViewType };

export const useNextDeliverySubscription = ({
  subscribableProducts,
  products,
  paramCategoryId,
}: Args) => {
  // ====== アコーディオン関連 ======
  const [isOpen, setIsOpen] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const height = document.querySelector('body')!.scrollHeight;
    resize(height);
  }, [isOpen]);

  useEffect(() => {
    const index = anchorLinkCategory(paramCategoryId, subscribableProducts);
    if (index === -1) return;

    const target = document.getElementById(`category_${index}`);
    if (target) {
      setTimeout(() => {
        const targetRect = target.getBoundingClientRect();
        scrollCategoryAccordion({ top: targetRect.top });
      }, 100);

      setIsOpen((prevState) => ({
        ...prevState,
        [index]: true,
      }));
    }
  }, [paramCategoryId, subscribableProducts]);

  const handleClickSwitchAccordion = useCallback(
    (categoryName: string, index: number, isOpen: boolean) => {
      setIsOpen((prevState) => ({
        ...prevState,
        [index]: isOpen,
      }));
      const eventName = isOpen ? 'click_open_' + categoryName : 'click_close_' + categoryName;
      postMessageGtag(eventName, 'detailProducts');
    },
    []
  );

  // ====== モーダル関連 ======
  const [productModal, setProductModal] = useState<ProductModal | null>(null);

  const closeModal = () => setProductModal(null);

  // 詳細を見る
  const handleClickProductLink = useCallback(
    async (mf: Product) => {
      const ingredientPage = mf.ingredientPage;
      const variantImages = ingredientVariantImages(products, ingredientPage);
      if (!variantImages) return;

      const pageInfo = await fetchGraphqlProductPage(ingredientPage, variantImages);
      if (!pageInfo || pageInfo.length === 0) return;

      const data: ProductDescModalViewType = {
        pageInfo,
        images: mf.variantImages,
        title: mf.title,
        setItems: mf.setItems,
      };
      setProductModal({ kind: 'description', data });
      postMessageGtag('click_modal_open_' + mf.handle, 'detailProducts');
    },
    [products]
  );

  // 商品を選ぶ
  const onClickBoxProductsSelect = useCallback(
    (box: SubscriptionBoxProduct, quantities: Record<string, number>) => {
      const data: BoxSelectProductsModalViewType = {
        items: box.items,
        title: box.parent.modalTitle ?? '',
        text: box.parent.modalText?.replace('{minQuantity}', String(box.parent.minQuantity)) ?? '',
        percent: box.parent.percent,
        minQuantity: box.parent.minQuantity,
        quantities,
      };
      setProductModal({ kind: 'boxUpdates', data });
    },
    []
  );

  // セット内容を見る（読み取り専用モーダル）
  const onClickBoxSetContents = useCallback(
    (box: SubscriptionBoxProduct, quantities: Record<string, number>) => {
      const lines = box.items
        .map((item) => ({
          displayName: item.title,
          quantity: quantities[item.id] ?? 0,
        }))
        .filter((line) => line.quantity > 0);
      let totalQuantity = 0;
      for (const line of lines) {
        totalQuantity += line.quantity;
      }
      const displayUnit =
        box.parent.displayUnit?.trim() ||
        box.items[0]?.displayUnit?.trim() ||
        '';
      const data: BoxSetContentsModalViewType = {
        lines,
        totalQuantity,
        displayUnit,
      };
      setProductModal({ kind: 'boxSetContents', data });
      postMessageGtag('click_modal_open_' + box.parent.handle, 'detailProducts');
    },
    []
  );

  return {
    isOpen,
    productModal,
    handleClickSwitchAccordion,
    closeModal,
    handleClickProductLink,
    onClickBoxProductsSelect,
    onClickBoxSetContents,
  };
};

function ingredientVariantImages(products: Products | null, pageHandle: string) {
  if (!products || !pageHandle) return null;

  const ret: Record<string, string> = {};

  //複数成分でない場合は必要ないため早期return
  const pageHandles = pageHandle.split('|');
  if (pageHandles.length <= 1) {
    ret[pageHandle] = '';
    return ret;
  }

  const ingredientPages: Record<string, string> = {};
  const ingredientPageSpares: Record<string, string> = {};

  for (const key in products) {
    const { ingredientPage, ingredientPageSpare, variantImage, displayUnit } = products[key];

    if (ingredientPage) {
      if (!ingredientPages[ingredientPage] || displayUnit === '袋') {
        ingredientPages[ingredientPage] = variantImage;
      }
    }

    if (ingredientPageSpare) {
      if (!ingredientPageSpares[ingredientPageSpare] || displayUnit === '袋') {
        ingredientPageSpares[ingredientPageSpare] = variantImage;
      }
    }
  }

  for (const handle of pageHandle.split('|')) {
    ret[handle] = ingredientPages[handle] ?? ingredientPageSpares[handle] ?? '';
  }

  return ret;
}

// アンカーリンク実施
function anchorLinkCategory(
  paramCategoryId: string,
  subscribableProducts: SubscribableProducts | null
) {
  if (!subscribableProducts) return -1;
  if (!paramCategoryId || paramCategoryId === 'coupon') return -1;

  const categories = Object.values(subscribableProducts.category);
  let index = -1;
  for (let i = 0; i < categories.length; i++) {
    if (categories[i].categoryId === paramCategoryId) {
      index = i;
      break;
    }
  }
  if (index === -1) return -1;

  return index;
}
